import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, Link2, Unlink, RefreshCw, CheckCircle, AlertCircle, 
  Loader2, ExternalLink, Clock, Inbox, Settings 
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { auth } from '../../utils/auth';
import { Button } from '../ui/button';
import { useDarkMode } from '../../context/DarkModeContext';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Microsoft Logo SVG
const MicrosoftLogo = () => (
  <svg width="24" height="24" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
    <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
  </svg>
);

const OutlookIntegration = ({ onConnectionChange }: { onConnectionChange?: (status: boolean) => void }) => {
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const [status, setStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => {
    fetchStatus();
    
    // Check for OAuth callback result
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('outlook_connected') === 'true') {
      toast.success('✅ Outlook tilkoblet!');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      fetchStatus();
    } else if (urlParams.get('outlook_error')) {
      toast.error(`Outlook-feil: ${urlParams.get('outlook_error')}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const fetchStatus = async () => {
    try {
      const token = auth.getToken();
      const response = await axios.get(`${API}/outlook/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
      if (onConnectionChange) {
        onConnectionChange(response.data.connected);
      }
    } catch (error) {
      console.error('Error fetching Outlook status:', error);
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const token = auth.getToken();
      const response = await axios.get(`${API}/outlook/connect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Microsoft OAuth
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error('Error starting Outlook connection:', error);
      toast.error('Kunne ikke starte Outlook-tilkobling');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Er du sikker på at du vil koble fra Outlook?')) return;
    
    setDisconnecting(true);
    try {
      const token = auth.getToken();
      await axios.delete(`${API}/outlook/disconnect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Outlook frakoblet');
      setStatus({ connected: false });
      if (onConnectionChange) {
        onConnectionChange(false);
      }
    } catch (error) {
      console.error('Error disconnecting Outlook:', error);
      toast.error('Kunne ikke koble fra Outlook');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const token = auth.getToken();
      const response = await axios.post(`${API}/outlook/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`✅ Synkronisert ${response.data.synced_count} nye e-poster`);
      fetchStatus();
    } catch (error) {
      console.error('Error syncing Outlook:', error);
      toast.error('Kunne ikke synkronisere e-poster');
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Aldri';
    const date = new Date(dateString);
    return date.toLocaleString('no-NO', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isConnected = status?.connected;

  if (loading) {
    return (
      <div className={`rounded-xl p-4 h-full ${
        isDarkMode 
          ? 'bg-white/5 border border-white/10' 
          : 'bg-white border border-gray-200'
      }`}>
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  // Compact card view for grid layout
  return (
    <div className={`rounded-xl overflow-hidden h-full ${
      isDarkMode 
        ? 'bg-white/5 border border-white/10' 
        : 'bg-white border border-gray-200'
    }`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-4 text-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
            <MicrosoftLogo />
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-base">Microsoft Outlook</h3>
            <p className="text-white/90 text-xs truncate">
              {isConnected ? status.outlook_email : 'E-post & Kalender'}
            </p>
          </div>
          {isConnected && (
            <CheckCircle className="w-5 h-5 ml-auto flex-shrink-0" />
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {isConnected ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Tilkoblet</span>
            </div>
            
            {/* Token Warning */}
            {status.token_expired && (
              <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 rounded px-2 py-1">
                <AlertCircle className="w-3 h-3" />
                <span>Token utløpt</span>
              </div>
            )}
            
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={handleSync}
                disabled={syncing}
                size="sm"
                className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-xs"
              >
                {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                <span className="ml-1">{syncing ? 'Synker...' : 'Synk'}</span>
              </Button>
              <Button
                onClick={() => navigate('/lexy/inbox')}
                size="sm"
                variant="outline"
                className="text-xs"
              >
                <Inbox className="w-3 h-3 mr-1" />
                Innboks
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Synk e-post og kalender
            </p>
            <Button
              onClick={handleConnect}
              disabled={connecting}
              size="sm"
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white"
            >
              {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
              <span className="ml-2">{connecting ? 'Kobler til...' : 'Koble til'}</span>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OutlookIntegration;
